import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../../i18n/LanguageContext';
import './AIDemo.css';

const AIDemo = () => {
  const { t } = useLanguage();
  const demos = t('demo.demos');
  const [activeDemo, setActiveDemo] = useState(0);
  const [displayedPrompt, setDisplayedPrompt] = useState('');
  const [displayedResponse, setDisplayedResponse] = useState('');
  const [phase, setPhase] = useState('idle'); // idle | typing-prompt | processing | typing-response | done
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: false });
  const timeoutRef = useRef(null);
  const hasStartedRef = useRef(false);

  const startDemo = useCallback((demoIndex) => {
    setDisplayedPrompt('');
    setDisplayedResponse('');
    setPhase('typing-prompt');
    setActiveDemo(demoIndex);
  }, []);

  // Start when section comes into view
  useEffect(() => {
    if (inView && !hasStartedRef.current) {
      hasStartedRef.current = true;
      setTimeout(() => startDemo(0), 600);
    }
  }, [inView, startDemo]);

  useEffect(() => {
    setActiveDemo(0);
    setDisplayedPrompt('');
    setDisplayedResponse('');
    setPhase('idle');
    hasStartedRef.current = false;
  }, [demos]);

  // Typing animation
  useEffect(() => {
    const demo = demos[activeDemo];

    if (phase === 'typing-prompt') {
      if (displayedPrompt.length < demo.prompt.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayedPrompt(demo.prompt.slice(0, displayedPrompt.length + 1));
        }, 35);
      } else {
        timeoutRef.current = setTimeout(() => setPhase('processing'), 400);
      }
    }

    if (phase === 'processing') {
      timeoutRef.current = setTimeout(() => setPhase('typing-response'), 1200);
    }

    if (phase === 'typing-response') {
      if (displayedResponse.length < demo.response.length) {
        // Faster typing for response
        const charsPerTick = 3;
        timeoutRef.current = setTimeout(() => {
          setDisplayedResponse(
            demo.response.slice(0, displayedResponse.length + charsPerTick)
          );
        }, 15);
      } else {
        setPhase('done');
      }
    }

    return () => clearTimeout(timeoutRef.current);
  }, [phase, displayedPrompt, displayedResponse, activeDemo, demos]);

  const handleDemoClick = (index) => {
    clearTimeout(timeoutRef.current);
    hasStartedRef.current = true;
    startDemo(index);
  };

  return (
    <section className="ai-demo" id="demo" ref={ref}>
      <div className="ai-demo__container">
        <div className="ai-demo__header">
          <span className="section-eyebrow" style={{ color: 'var(--cyan)' }}>
            {t('demo.eyebrow')}
          </span>
          <h2 className="section-title">{t('demo.title')}</h2>
          <p className="ai-demo__subtitle">
            {t('demo.subtitle')}
          </p>
          <div className="ai-demo__meta">
            {t('demo.metaPills').map((pill) => (
              <span key={pill} className="ai-demo__meta-pill">
                {pill}
              </span>
            ))}
          </div>
        </div>

        <div className="ai-demo__tabs">
          {demos.map((demo, i) => (
            <button
              key={i}
              className={`ai-demo__tab ${activeDemo === i ? 'ai-demo__tab--active' : ''}`}
              onClick={() => handleDemoClick(i)}
            >
              <span className="ai-demo__tab-dot"></span>
              {demo.prompt.split(' ').slice(0, 3).join(' ')}...
            </button>
          ))}
        </div>

        <div className="ai-demo__terminal">
          <div className="ai-demo__terminal-header">
            <div className="ai-demo__terminal-dots">
              <span className="ai-demo__dot ai-demo__dot--red"></span>
              <span className="ai-demo__dot ai-demo__dot--yellow"></span>
              <span className="ai-demo__dot ai-demo__dot--green"></span>
            </div>
            <span className="ai-demo__terminal-title">{t('demo.terminalTitle')}</span>
            <div className="ai-demo__terminal-status">
              <span className="ai-demo__status-dot"></span>
              {t('demo.statusConnected')}
            </div>
          </div>

          <div className="ai-demo__terminal-body">
            <div className="ai-demo__line">
              <span className="ai-demo__prompt-sign">></span>
              <span className="ai-demo__prompt-text">
                {displayedPrompt}
                {phase === 'typing-prompt' && <span className="ai-demo__cursor">|</span>}
              </span>
            </div>

            {(phase === 'processing' || phase === 'typing-response' || phase === 'done') && (
              <div className="ai-demo__response-area">
                {phase === 'processing' && (
                  <div className="ai-demo__loader">
                    <span className="ai-demo__loader-dot"></span>
                    <span className="ai-demo__loader-dot"></span>
                    <span className="ai-demo__loader-dot"></span>
                    <span className="ai-demo__loader-text">{t('demo.loaderText')}</span>
                  </div>
                )}
                {(phase === 'typing-response' || phase === 'done') && (
                  <pre className="ai-demo__response-text">
                    {displayedResponse}
                    {phase === 'typing-response' && <span className="ai-demo__cursor">|</span>}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIDemo;
